import { el, clear } from "@utils/dom";
import { nid } from "@utils/id";
import { getState, setState, subscribe } from "@state/store";
import { pushSeatingHistory } from "@state/seatingHistory";
import type { Table, TableShape, SeatingChart, Student, Seat, ReferenceMarker, ReferenceMarkerType, FocalEdge } from "../../types/models";
import { checkAssignmentConstraints } from "@logic/seating/index";
import { getTableSize, calculateSeatPositions } from "./SeatingChartEditor";
import { generateInitials, stringToColor } from "@utils/avatar";

// Theme-aware table colors (work well in both light and dark modes)
const TABLE_COLORS_LIGHT = [
    "#e8f4f8", "#f8e8f4", "#f4f8e8", "#f8f4e8",
    "#e8e8f8", "#f8e8e8", "#e8f8e8", "#f4e8f8"
];

const TABLE_COLORS_DARK = [
    "#1a3a4a", "#3a1a3a", "#2a3a1a", "#3a3a1a",
    "#2a2a4a", "#3a2a2a", "#1a3a2a", "#3a1a4a"
];

function getTableColors(): string[] {
    const isDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    return isDark ? TABLE_COLORS_DARK : TABLE_COLORS_LIGHT;
}

/**
 * Snap a point to the edge of a table using raycast approach
 */
function snapToEdge(
    mouseX: number,
    mouseY: number,
    cx: number,
    cy: number,
    width: number,
    height: number,
    shape: 'round' | 'rectangular'
): { x: number; y: number } {
    const padding = 20;

    if (shape === 'round') {
        const radius = Math.min(width, height) / 2;
        const dx = mouseX - cx;
        const dy = mouseY - cy;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < 0.001) {
            return { x: cx + radius + padding, y: cy };
        }

        const nx = dx / dist;
        const ny = dy / dist;
        return {
            x: cx + nx * (radius + padding),
            y: cy + ny * (radius + padding)
        };
    } else {
        const numRays = 360;
        let bestPoint = { x: width + padding, y: cy };
        let bestDist = Infinity;

        const toTableX = cx - mouseX;
        const toTableY = cy - mouseY;
        const centerAngle = Math.atan2(toTableY, toTableX);

        for (let i = 0; i < numRays; i++) {
            const offsetAngle = ((i / (numRays - 1)) - 0.5) * Math.PI;
            const angle = centerAngle + offsetAngle;
            const dirX = Math.cos(angle);
            const dirY = Math.sin(angle);

            const hits: { x: number; y: number; t: number }[] = [];

            if (Math.abs(dirY) > 0.0001) {
                const t = -mouseY / dirY;
                if (t > 0) {
                    const x = mouseX + t * dirX;
                    if (x >= 0 && x <= width) {
                        hits.push({ x, y: -padding, t });
                    }
                }
            }

            if (Math.abs(dirY) > 0.0001) {
                const t = (height - mouseY) / dirY;
                if (t > 0) {
                    const x = mouseX + t * dirX;
                    if (x >= 0 && x <= width) {
                        hits.push({ x, y: height + padding, t });
                    }
                }
            }

            if (Math.abs(dirX) > 0.0001) {
                const t = -mouseX / dirX;
                if (t > 0) {
                    const y = mouseY + t * dirY;
                    if (y >= 0 && y <= height) {
                        hits.push({ x: -padding, y, t });
                    }
                }
            }

            if (Math.abs(dirX) > 0.0001) {
                const t = (width - mouseX) / dirX;
                if (t > 0) {
                    const y = mouseY + t * dirY;
                    if (y >= 0 && y <= height) {
                        hits.push({ x: width + padding, y, t });
                    }
                }
            }

            for (const hit of hits) {
                if (hit.t < bestDist) {
                    bestDist = hit.t;
                    bestPoint = { x: hit.x, y: hit.y };
                }
            }
        }

        return bestPoint;
    }
}

/**
 * Create the seating chart view component with integrated editing
 */
export function createSeatingChartView(): HTMLElement {
    const container = el("div", { class: "seating-chart-view" });

    // Dragging state
    let draggedTable: string | null = null;
    let dragOffset = { x: 0, y: 0 };
    let draggedSeat: { tableId: string; seatId: string } | null = null;
    let seatDragOffset = { x: 0, y: 0 };
    let resizingTable: { tableId: string; corner: string; startX: number; startY: number; startWidth: number; startHeight: number; startTableX: number; startTableY: number } | null = null;
    let draggedMarker: string | null = null;
    let markerDragOffset = { x: 0, y: 0 };

    // Zoom and pan state
    let zoomLevel = 1.0;
    let isPanning = false;
    let panStart = { x: 0, y: 0 };
    let panX = 0;
    let panY = 0;

    function render() {
        clear(container);

        const s = getState();
        const cls = s.classes.find(c => c.id === s.activeClassId);
        if (!cls) return;

        let chart = cls.seatingCharts?.find(c => c.id === cls.activeChartId)
            ?? cls.seatingCharts?.[0];

        // Auto-create a chart if none exists
        if (!chart) {
            const newChart: SeatingChart = {
                id: nid("chart_"),
                name: "Seating Chart",
                tables: [],
                proximityConstraints: [],
                referenceMarkers: []
            };
            setState(ns => {
                const c = ns.classes.find(x => x.id === s.activeClassId);
                if (c) {
                    if (!c.seatingCharts) c.seatingCharts = [];
                    c.seatingCharts.push(newChart);
                    c.activeChartId = newChart.id;
                }
            });
            chart = newChart;
        }

        // Toolbar
        container.appendChild(renderToolbar(chart));

        // Stats bar
        container.appendChild(renderStats(chart, cls.students));

        // Violations summary (only show teacher constraint violations, not admin ones)
        if (chart.lastAssignment) {
            const allViolations = checkAssignmentConstraints(chart.lastAssignment, chart, cls.students);
            const teacherViolations = allViolations.filter(v => v.constraint.source !== 'admin');
            if (teacherViolations.length > 0) {
                container.appendChild(renderViolations(teacherViolations));
            }
        }

        // Calculate content bounds for the canvas
        const getContentBounds = () => {
            let maxX = 800, maxY = 500; // Minimum size
            for (const table of chart.tables) {
                const size = getTableSize(table);
                maxX = Math.max(maxX, table.x + size.width + 100);
                maxY = Math.max(maxY, table.y + size.height + 100);
            }
            if (chart.referenceMarkers) {
                for (const marker of chart.referenceMarkers) {
                    const w = marker.width ?? 100;
                    const h = marker.height ?? 60;
                    maxX = Math.max(maxX, marker.x + w + 100);
                    maxY = Math.max(maxY, marker.y + h + 100);
                }
            }
            return { width: maxX, height: maxY };
        };

        const contentBounds = getContentBounds();

        // Chart wrapper - fixed visual size viewport with border
        const chartWrapper = el("div", {
            class: "chart-wrapper",
            style: "position: relative; height: 500px; background: var(--bg); border: 2px dashed var(--border); border-radius: 12px; overflow: hidden;"
        });

        // Chart container - fixed workspace size (does not scale)
        const chartContainer = el("div", {
            class: "chart-container",
            style: `position: relative; width: ${contentBounds.width}px; height: ${contentBounds.height}px; transform: translate(${panX}px, ${panY}px);`
        });

        // Chart content - scales with zoom, workspace size remains constant
        const chartContent = el("div", {
            class: "chart-content",
            style: `position: relative; width: 100%; height: 100%; transform: scale(${zoomLevel}); transform-origin: top left;`
        });

        // Update chart transform
        const updateChartTransform = () => {
            const bounds = getContentBounds();
            // Set the container size based on content
            chartContainer.style.width = `${bounds.width}px`;
            chartContainer.style.height = `${bounds.height}px`;
            chartContent.style.transform = `scale(${zoomLevel})`;
            chartContainer.style.transform = `translate(${panX}px, ${panY}px)`;
        };

        // Zoom with mouse wheel (scroll to zoom)
        chartWrapper.addEventListener("wheel", (e: WheelEvent) => {
            // Wheel = zoom
            e.preventDefault();
            const delta = e.deltaY > 0 ? -0.1 : 0.1;
            zoomLevel = Math.max(0.25, Math.min(2.0, zoomLevel + delta));
            updateChartTransform();
        }, { passive: false });

        // Pan with left-click drag on empty area
        chartWrapper.addEventListener("mousedown", (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            // Check if clicking on empty area (wrapper or container background)
            const isEmptyArea = target === chartWrapper || target === chartContainer || target === chartContent || target.classList.contains('seating-empty-state');

            // Left-click on empty area, or middle-click anywhere
            if ((e.button === 0 && isEmptyArea) || e.button === 1) {
                e.preventDefault();
                isPanning = true;
                panStart = { x: e.clientX - panX, y: e.clientY - panY };
                chartWrapper.style.cursor = "grabbing";
            }
        });

        chartWrapper.addEventListener("mousemove", (e: MouseEvent) => {
            if (isPanning) {
                panX = e.clientX - panStart.x;
                panY = e.clientY - panStart.y;
                updateChartTransform();
            }
        });

        chartWrapper.addEventListener("mouseup", () => {
            if (isPanning) {
                isPanning = false;
                chartWrapper.style.cursor = "";
            }
        });

        chartWrapper.addEventListener("mouseleave", () => {
            if (isPanning) {
                isPanning = false;
                chartWrapper.style.cursor = "";
            }
        });

        // Handle toolbar drops
        setupCanvasDropHandlers(chartContainer, chart, chartWrapper, chartContent);

        if (chart.tables.length === 0 && (!chart.referenceMarkers || chart.referenceMarkers.length === 0)) {
            chartContent.appendChild(
                el("div", {
                    class: "seating-empty-state",
                    style: "position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); text-align: center; color: var(--muted);"
                },
                    el("span", { style: "font-size: 2rem; display: block; margin-bottom: 8px;" }, "📋"),
                    el("p", {}, "Drag tables from the toolbar above"),
                    el("p", { style: "font-size: 0.875rem;" }, "Then drag chairs onto tables to add seats")
                )
            );
        }

        // Render reference markers
        if (chart.referenceMarkers) {
            for (const marker of chart.referenceMarkers) {
                chartContent.appendChild(renderReferenceMarker(marker, chart));
            }
        }

        // Render tables with seats
        for (const table of chart.tables) {
            chartContent.appendChild(renderTableWithSeats(table, chart, cls.students));
        }

        // Setup mouse handlers for dragging
        setupMouseHandlers(chartContent, chart);

        chartContainer.appendChild(chartContent);
        chartWrapper.appendChild(chartContainer);
        container.appendChild(chartWrapper);

        // Unassigned students
        const unassignedStudents = getUnassignedStudents(chart, cls.students);
        if (unassignedStudents.length > 0) {
            container.appendChild(renderUnassignedStudents(unassignedStudents, chart));
        }
    }

    function renderToolbar(chart: SeatingChart): HTMLElement {
        const toolbar = el("div", {
            class: "editor-toolbar",
            style: `
                display: flex;
                gap: 12px;
                padding: 10px 14px;
                background: var(--card);
                border: 1px solid var(--border);
                border-radius: 8px;
                margin-bottom: 12px;
                align-items: center;
                flex-wrap: wrap;
            `
        });

        const toolbarLabel = el("span", {
            style: "font-size: 0.8rem; color: var(--muted); margin-right: 4px;"
        }, "Drag to add:");

        // Round table
        const roundTableItem = createToolbarItem("round-table", "Round", `
            width: 40px; height: 40px; border-radius: 50%;
            background: var(--bg); border: 2px solid var(--border);
        `);

        // Rectangular table
        const rectTableItem = createToolbarItem("rect-table", "Rect", `
            width: 55px; height: 35px; border-radius: 4px;
            background: var(--bg); border: 2px solid var(--border);
        `);

        // Chair
        const chairItem = createToolbarItem("chair", "🪑", `
            width: 32px; height: 32px; border-radius: 50%;
            background: var(--bg); border: 2px dashed var(--border);
        `);

        // Separator
        const separator = el("div", {
            style: "width: 1px; height: 30px; background: var(--border); margin: 0 4px;"
        });

        // Reference label
        const refLabel = el("span", {
            style: "font-size: 0.8rem; color: var(--muted); margin-right: 4px;"
        }, "Reference:");

        // Teacher's desk
        const deskItem = createToolbarItem("desk", "🖥️", `
            width: 50px; height: 30px; border-radius: 4px;
            background: var(--bg); border: 2px solid var(--border);
        `);
        deskItem.title = "Teacher's Desk";

        // Door
        const doorItem = createToolbarItem("door", "🚪", `
            width: 30px; height: 40px; border-radius: 4px;
            background: var(--bg); border: 2px solid var(--border);
        `);
        doorItem.title = "Door";

        // Separator 2
        const separator2 = el("div", {
            style: "width: 1px; height: 30px; background: var(--border); margin: 0 4px;"
        });

        // Focal edge selector
        const focalLabel = el("span", {
            style: "font-size: 0.8rem; color: var(--muted); margin-right: 4px;"
        }, "Fill from:");

        const focalSelect = el("select", {
            style: "font-size: 0.8rem; padding: 4px 8px; border-radius: 4px; border: 1px solid var(--border); background: var(--bg); color: var(--fg);"
        }) as HTMLSelectElement;

        const focalOptions: { value: FocalEdge; label: string }[] = [
            { value: 'none', label: 'No preference' },
            { value: 'top', label: '↑ Top edge' },
            { value: 'bottom', label: '↓ Bottom edge' },
            { value: 'left', label: '← Left edge' },
            { value: 'right', label: '→ Right edge' }
        ];

        for (const opt of focalOptions) {
            const option = el("option", { value: opt.value }, opt.label);
            if (chart.focalEdge === opt.value || (!chart.focalEdge && opt.value === 'none')) {
                (option as HTMLOptionElement).selected = true;
            }
            focalSelect.appendChild(option);
        }

        focalSelect.addEventListener("change", () => {
            setState(ns => {
                const c = ns.classes.find(x => x.id === ns.activeClassId);
                const ch = c?.seatingCharts?.find(x => x.id === c.activeChartId);
                if (ch) {
                    ch.focalEdge = focalSelect.value as FocalEdge;
                }
            });
        });

        // Separator 3
        const separator3 = el("div", {
            style: "width: 1px; height: 30px; background: var(--border); margin: 0 4px;"
        });

        // Zoom controls
        const zoomLabel = el("span", {
            style: "font-size: 0.8rem; color: var(--muted); margin-right: 4px;"
        }, "Zoom:");

        const zoomOutBtn = el("button", {
            style: "padding: 4px 8px; font-size: 12px;",
            title: "Zoom out"
        }, "−");
        zoomOutBtn.addEventListener("click", () => {
            zoomLevel = Math.max(0.25, zoomLevel - 0.25);
            render();
        });

        const zoomDisplay = el("span", {
            style: "font-size: 0.8rem; min-width: 40px; text-align: center;"
        }, `${Math.round(zoomLevel * 100)}%`);

        const zoomInBtn = el("button", {
            style: "padding: 4px 8px; font-size: 12px;",
            title: "Zoom in"
        }, "+");
        zoomInBtn.addEventListener("click", () => {
            zoomLevel = Math.min(2.0, zoomLevel + 0.25);
            render();
        });

        const fitBtn = el("button", {
            style: "padding: 4px 8px; font-size: 11px;",
            title: "Fit to view"
        }, "Fit");
        fitBtn.addEventListener("click", () => {
            zoomLevel = 1.0;
            render();
        });

        // Copy chart button
        const copyChartBtn = el("button", {
            style: "padding: 4px 8px; font-size: 11px; margin-left: auto;",
            title: "Copy chart to another class"
        }, "📋 Copy to...");
        copyChartBtn.addEventListener("click", () => openCopyChartDialog(chart));

        toolbar.append(
            toolbarLabel, roundTableItem, rectTableItem, chairItem,
            separator, refLabel, deskItem, doorItem,
            separator2, focalLabel, focalSelect,
            separator3, zoomLabel, zoomOutBtn, zoomDisplay, zoomInBtn, fitBtn,
            copyChartBtn
        );
        return toolbar;
    }

    function openCopyChartDialog(chart: SeatingChart) {
        const s = getState();
        const otherClasses = s.classes.filter(c => c.id !== s.activeClassId);

        if (otherClasses.length === 0) {
            alert("No other classes to copy to. Create another class first.");
            return;
        }

        const backdrop = el("div", {
            style: "position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 1000; display: flex; align-items: center; justify-content: center;"
        });

        const dialog = el("div", {
            style: "background: var(--card); border-radius: 12px; padding: 20px; min-width: 300px; border: 1px solid var(--border);"
        });

        const title = el("h3", { style: "margin: 0 0 16px 0;" }, "Copy Chart to Class");

        const selectEl = el("select", { style: "width: 100%; padding: 8px; margin-bottom: 12px;" }) as HTMLSelectElement;
        for (const cls of otherClasses) {
            selectEl.appendChild(el("option", { value: cls.id }, cls.name));
        }

        const nameInput = el("input", {
            type: "text",
            value: `${chart.name} (copy)`,
            placeholder: "New chart name",
            style: "width: 100%; padding: 8px; margin-bottom: 12px; box-sizing: border-box;"
        }) as HTMLInputElement;

        const includeConstraintsLabel = el("label", { style: "display: flex; align-items: center; gap: 8px; margin-bottom: 16px;" });
        const includeConstraintsCheckbox = el("input", { type: "checkbox" }) as HTMLInputElement;
        includeConstraintsLabel.append(includeConstraintsCheckbox, el("span", {}, "Include proximity constraints"));

        const buttons = el("div", { style: "display: flex; gap: 8px; justify-content: flex-end;" });

        const cancelBtn = el("button", {}, "Cancel");
        cancelBtn.addEventListener("click", () => backdrop.remove());

        const copyBtn = el("button", { class: "primary" }, "Copy");
        copyBtn.addEventListener("click", () => {
            const targetClassId = selectEl.value;
            const newName = nameInput.value.trim() || `${chart.name} (copy)`;
            const includeConstraints = includeConstraintsCheckbox.checked;

            const newChart: SeatingChart = {
                id: nid("chart_"),
                name: newName,
                tables: chart.tables.map(t => {
                    const newTable: Table = {
                        id: nid("tbl_"),
                        name: t.name,
                        shape: t.shape,
                        x: t.x,
                        y: t.y,
                        seats: t.seats.map(seat => {
                            const newSeat: Seat = {
                                id: nid("seat_"),
                                position: seat.position
                            };
                            if (seat.x !== undefined) newSeat.x = seat.x;
                            if (seat.y !== undefined) newSeat.y = seat.y;
                            return newSeat;
                        })
                    };
                    if (t.width !== undefined) newTable.width = t.width;
                    if (t.height !== undefined) newTable.height = t.height;
                    if (t.rotation !== undefined) newTable.rotation = t.rotation;
                    return newTable;
                }),
                proximityConstraints: includeConstraints ? chart.proximityConstraints.map(pc => ({
                    ...pc,
                    id: nid("prox_")
                })) : []
            };
            if (chart.referenceMarkers) {
                newChart.referenceMarkers = chart.referenceMarkers.map(m => ({
                    ...m,
                    id: nid("ref_")
                }));
            }
            if (chart.focalEdge !== undefined) {
                newChart.focalEdge = chart.focalEdge;
            }

            setState(ns => {
                const c = ns.classes.find(x => x.id === targetClassId);
                if (c) {
                    if (!c.seatingCharts) c.seatingCharts = [];
                    c.seatingCharts.push(newChart);
                }
            });

            backdrop.remove();
            alert(`Chart copied to ${otherClasses.find(c => c.id === targetClassId)?.name}`);
        });

        buttons.append(cancelBtn, copyBtn);
        dialog.append(title, selectEl, nameInput, includeConstraintsLabel, buttons);
        backdrop.appendChild(dialog);
        document.body.appendChild(backdrop);

        backdrop.addEventListener("click", (e) => {
            if (e.target === backdrop) backdrop.remove();
        });
    }

    function createToolbarItem(type: string, label: string, extraStyle: string): HTMLElement {
        const item = el("div", {
            class: "toolbar-item",
            draggable: "true",
            "data-type": type,
            style: `
                ${extraStyle}
                cursor: grab;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 10px;
                color: var(--muted);
                transition: transform 0.15s, box-shadow 0.15s;
            `
        }, label);

        item.addEventListener("dragstart", (e: DragEvent) => {
            e.dataTransfer?.setData("item-type", type);
            item.style.opacity = "0.5";
        });
        item.addEventListener("dragend", () => {
            item.style.opacity = "1";
        });
        item.addEventListener("mouseenter", () => {
            item.style.transform = "scale(1.05)";
            item.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)";
        });
        item.addEventListener("mouseleave", () => {
            item.style.transform = "scale(1)";
            item.style.boxShadow = "none";
        });

        return item;
    }

    function setupCanvasDropHandlers(canvas: HTMLElement, chart: SeatingChart, chartWrapper: HTMLElement, chartContent: HTMLElement) {
        canvas.addEventListener("dragover", (e: DragEvent) => {
            e.preventDefault();
            const itemType = e.dataTransfer?.types.includes("item-type");
            if (itemType) {
                canvas.style.borderColor = "var(--primary)";
            }
        });

        canvas.addEventListener("dragleave", (e: DragEvent) => {
            const related = e.relatedTarget as HTMLElement;
            if (!canvas.contains(related)) {
                canvas.style.borderColor = "var(--border)";
            }
        });

        canvas.addEventListener("drop", (e: DragEvent) => {
            e.preventDefault();
            canvas.style.borderColor = "var(--border)";

            const itemType = e.dataTransfer?.getData("item-type");
            if (!itemType) return;

            const canvasRect = canvas.getBoundingClientRect();
            // Adjust for zoom level and scroll position when calculating drop position
            const x = ((e.clientX - canvasRect.left) + chartWrapper.scrollLeft) / zoomLevel - 50;
            const y = ((e.clientY - canvasRect.top) + chartWrapper.scrollTop) / zoomLevel - 50;

            if (itemType === "round-table" || itemType === "rect-table") {
                const shape: TableShape = itemType === "round-table" ? "round" : "rectangular";
                const newTable: Table = {
                    id: nid("tbl_"),
                    name: `Table ${chart.tables.length + 1}`,
                    shape,
                    seats: [],
                    x: Math.max(0, x),
                    y: Math.max(0, y)
                };

                setState(ns => {
                    const c = ns.classes.find(x => x.id === getState().activeClassId);
                    const ch = c?.seatingCharts?.find(x => x.id === chart.id);
                    if (ch) ch.tables.push(newTable);
                });
                render();
            } else if (itemType === "desk" || itemType === "door") {
                const markerType: ReferenceMarkerType = itemType as ReferenceMarkerType;
                const newMarker: ReferenceMarker = {
                    id: nid("ref_"),
                    type: markerType,
                    x: Math.max(0, x),
                    y: Math.max(0, y),
                    width: markerType === "desk" ? 100 : 40,
                    height: markerType === "desk" ? 50 : 60
                };

                setState(ns => {
                    const c = ns.classes.find(x => x.id === getState().activeClassId);
                    const ch = c?.seatingCharts?.find(x => x.id === chart.id);
                    if (ch) {
                        if (!ch.referenceMarkers) ch.referenceMarkers = [];
                        ch.referenceMarkers.push(newMarker);
                    }
                });
                render();
            }
        });
    }

    function renderReferenceMarker(marker: ReferenceMarker, chart: SeatingChart): HTMLElement {
        const width = marker.width ?? (marker.type === "desk" ? 100 : 40);
        const height = marker.height ?? (marker.type === "desk" ? 50 : 60);

        const markerEl = el("div", {
            class: `reference-marker ${marker.type}`,
            "data-marker-id": marker.id,
            style: `
                position: absolute;
                left: ${marker.x}px;
                top: ${marker.y}px;
                width: ${width}px;
                height: ${height}px;
                background: var(--card);
                border: 2px solid var(--border);
                border-radius: 6px;
                cursor: move;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-direction: column;
                font-size: 20px;
                opacity: 0.8;
            `
        });

        markerEl.appendChild(el("span", {}, marker.type === "desk" ? "🖥️" : "🚪"));
        markerEl.appendChild(el("span", { style: "font-size: 9px; color: var(--muted);" },
            marker.type === "desk" ? "Desk" : "Door"));

        // Delete button
        const deleteBtn = el("button", {
            style: "position: absolute; top: -8px; right: -8px; width: 18px; height: 18px; border-radius: 50%; background: var(--danger); color: white; border: 2px solid white; font-size: 10px; cursor: pointer; opacity: 0; transition: opacity 0.2s;",
            onclick: (e: Event) => {
                e.stopPropagation();
                setState(ns => {
                    const c = ns.classes.find(x => x.id === getState().activeClassId);
                    const ch = c?.seatingCharts?.find(x => x.id === chart.id);
                    if (ch?.referenceMarkers) {
                        ch.referenceMarkers = ch.referenceMarkers.filter(m => m.id !== marker.id);
                    }
                });
                render();
            }
        }, "×");
        markerEl.appendChild(deleteBtn);

        markerEl.addEventListener("mouseenter", () => { deleteBtn.style.opacity = "1"; });
        markerEl.addEventListener("mouseleave", () => { deleteBtn.style.opacity = "0"; });

        // Drag handling
        markerEl.addEventListener("mousedown", (e: MouseEvent) => {
            draggedMarker = marker.id;
            const rect = markerEl.getBoundingClientRect();
            // Adjust drag offset for zoom level
            markerDragOffset = { x: (e.clientX - rect.left) / zoomLevel, y: (e.clientY - rect.top) / zoomLevel };
            markerEl.style.zIndex = "100";
        });

        return markerEl;
    }

    function renderStats(chart: SeatingChart, students: Student[]): HTMLElement {
        const presentStudents = students.filter(s => !s.absent);
        const totalSeats = chart.tables.reduce((sum, t) => sum + t.seats.length, 0);
        const assignedCount = chart.lastAssignment
            ? Array.from(chart.lastAssignment.seatAssignments.values()).filter(Boolean).length
            : 0;

        return el("div", { class: "chart-stats" },
            el("div", { class: "chart-stat" },
                "Tables: ",
                el("span", { class: "chart-stat-value" }, String(chart.tables.length))
            ),
            el("div", { class: "chart-stat" },
                "Seats: ",
                el("span", { class: "chart-stat-value" }, String(totalSeats))
            ),
            el("div", { class: "chart-stat" },
                "Assigned: ",
                el("span", { class: "chart-stat-value" }, `${assignedCount}/${presentStudents.length}`)
            )
        );
    }

    function renderViolations(violations: any[]): HTMLElement {
        const container = el("div", { class: "violations-summary" });
        container.appendChild(el("h4", {}, `${violations.length} Constraint Violation${violations.length > 1 ? 's' : ''}`));

        const list = el("ul", { class: "violations-list" });
        for (const v of violations.slice(0, 5)) {
            list.appendChild(el("li", {}, v.message));
        }
        if (violations.length > 5) {
            list.appendChild(el("li", {}, `...and ${violations.length - 5} more`));
        }

        container.appendChild(list);
        return container;
    }

    function renderTableWithSeats(table: Table, chart: SeatingChart, students: Student[]): HTMLElement {
        const size = getTableSize(table);
        const tableColors = getTableColors();
        const colorIndex = parseInt(table.id.slice(-2), 36) % tableColors.length;
        const bgColor = tableColors[colorIndex];

        const rotation = table.rotation ?? 0;
        const tableEl = el("div", {
            class: `seating-table ${table.shape}`,
            "data-table-id": table.id,
            style: `
                position: absolute;
                left: ${table.x}px;
                top: ${table.y}px;
                width: ${size.width}px;
                height: ${size.height}px;
                background: ${bgColor};
                border: 2px solid var(--border);
                border-radius: ${table.shape === 'round' ? '50%' : '8px'};
                cursor: move;
                transform: rotate(${rotation}deg);
                transform-origin: center center;
            `
        });

        // Rotation handle (only for rectangular tables)
        if (table.shape === 'rectangular') {
            const rotateHandle = el("div", {
                class: "rotate-handle",
                title: "Drag to rotate",
                style: `
                    position: absolute;
                    top: -25px;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 20px;
                    height: 20px;
                    background: var(--primary);
                    border: 2px solid white;
                    border-radius: 50%;
                    cursor: grab;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 10px;
                    color: white;
                    opacity: 0;
                    transition: opacity 0.2s;
                    z-index: 20;
                `
            }, "↻");

            let isRotating = false;
            let startAngle = 0;
            let startRotation = rotation;

            rotateHandle.addEventListener("mousedown", (e: MouseEvent) => {
                e.stopPropagation();
                isRotating = true;

                const rect = tableEl.getBoundingClientRect();
                const cx = rect.left + rect.width / 2;
                const cy = rect.top + rect.height / 2;

                startAngle = Math.atan2(e.clientY - cy, e.clientX - cx) * (180 / Math.PI);
                startRotation = table.rotation ?? 0;

                const onMouseMove = (e: MouseEvent) => {
                    if (!isRotating) return;

                    const currentAngle = Math.atan2(e.clientY - cy, e.clientX - cx) * (180 / Math.PI);
                    let newRotation = startRotation + (currentAngle - startAngle);

                    // Snap to 15 degree increments if shift is held
                    if (e.shiftKey) {
                        newRotation = Math.round(newRotation / 15) * 15;
                    }

                    tableEl.style.transform = `rotate(${newRotation}deg)`;
                };

                const onMouseUp = () => {
                    if (!isRotating) return;
                    isRotating = false;

                    const currentTransform = tableEl.style.transform;
                    const match = currentTransform.match(/rotate\(([^)]+)deg\)/);
                    const finalRotation = match && match[1] ? parseFloat(match[1]) : 0;

                    setState(ns => {
                        const c = ns.classes.find(x => x.id === getState().activeClassId);
                        const ch = c?.seatingCharts?.find(x => x.id === chart.id);
                        const t = ch?.tables.find(x => x.id === table.id);
                        if (t) t.rotation = finalRotation;
                    });

                    document.removeEventListener("mousemove", onMouseMove);
                    document.removeEventListener("mouseup", onMouseUp);
                };

                document.addEventListener("mousemove", onMouseMove);
                document.addEventListener("mouseup", onMouseUp);
            });

            tableEl.appendChild(rotateHandle);

            tableEl.addEventListener("mouseenter", () => { rotateHandle.style.opacity = "1"; });
            tableEl.addEventListener("mouseleave", () => { if (!isRotating) rotateHandle.style.opacity = "0"; });
        }

        // Clone button
        const cloneBtn = el("button", {
            style: "position: absolute; top: -8px; left: -8px; width: 20px; height: 20px; border-radius: 50%; background: var(--primary); color: white; border: 2px solid white; font-size: 10px; cursor: pointer; opacity: 0; transition: opacity 0.2s;",
            title: "Clone table",
            onclick: (e: Event) => {
                e.stopPropagation();
                setState(ns => {
                    const c = ns.classes.find(x => x.id === getState().activeClassId);
                    const ch = c?.seatingCharts?.find(x => x.id === chart.id);
                    if (!ch) return;

                    const clonedTable: Table = {
                        id: nid("tbl_"),
                        name: `${table.name} (copy)`,
                        shape: table.shape,
                        x: table.x + 30,
                        y: table.y + 30,
                        seats: table.seats.map(seat => {
                            const clonedSeat: Seat = {
                                id: nid("seat_"),
                                position: seat.position
                            };
                            if (seat.x !== undefined) clonedSeat.x = seat.x;
                            if (seat.y !== undefined) clonedSeat.y = seat.y;
                            return clonedSeat;
                        })
                    };
                    if (table.width !== undefined) clonedTable.width = table.width;
                    if (table.height !== undefined) clonedTable.height = table.height;

                    ch.tables.push(clonedTable);
                });
                render();
            }
        }, "⧉");
        tableEl.appendChild(cloneBtn);

        // Delete button
        const deleteBtn = el("button", {
            style: "position: absolute; top: -8px; right: -8px; width: 20px; height: 20px; border-radius: 50%; background: var(--danger); color: white; border: 2px solid white; font-size: 12px; cursor: pointer; opacity: 0; transition: opacity 0.2s;",
            onclick: (e: Event) => {
                e.stopPropagation();
                setState(ns => {
                    const c = ns.classes.find(x => x.id === getState().activeClassId);
                    const ch = c?.seatingCharts?.find(x => x.id === chart.id);
                    if (ch) ch.tables = ch.tables.filter(t => t.id !== table.id);
                });
                render();
            }
        }, "×");
        tableEl.appendChild(deleteBtn);

        tableEl.addEventListener("mouseenter", () => { cloneBtn.style.opacity = "1"; deleteBtn.style.opacity = "1"; });
        tableEl.addEventListener("mouseleave", () => { cloneBtn.style.opacity = "0"; deleteBtn.style.opacity = "0"; });

        // Chair drop handling
        tableEl.addEventListener("dragover", (e: DragEvent) => {
            const itemType = e.dataTransfer?.types.includes("item-type");
            if (itemType) {
                e.preventDefault();
                e.stopPropagation();
                tableEl.style.boxShadow = "0 0 0 3px var(--primary)";
            }
        });

        tableEl.addEventListener("dragleave", (e: DragEvent) => {
            const relatedTarget = e.relatedTarget as HTMLElement;
            if (!tableEl.contains(relatedTarget)) {
                tableEl.style.boxShadow = "none";
            }
        });

        tableEl.addEventListener("drop", (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            tableEl.style.boxShadow = "none";

            const itemType = e.dataTransfer?.getData("item-type");
            if (itemType !== "chair") return;

            const tableRect = tableEl.getBoundingClientRect();
            // Adjust for zoom level
            const dropX = (e.clientX - tableRect.left) / zoomLevel;
            const dropY = (e.clientY - tableRect.top) / zoomLevel;

            const newSeat: Seat = {
                id: nid("seat_"),
                position: table.seats.length,
                x: dropX - size.width / 2,
                y: dropY - size.height / 2
            };

            setState(ns => {
                const c = ns.classes.find(x => x.id === getState().activeClassId);
                const ch = c?.seatingCharts?.find(x => x.id === chart.id);
                const t = ch?.tables.find(x => x.id === table.id);
                if (t) t.seats.push(newSeat);
            });
            render();
        });

        // Resize handles
        const corners = ['nw', 'ne', 'sw', 'se'];
        for (const corner of corners) {
            const handle = el("div", {
                class: `resize-handle resize-${corner}`,
                "data-corner": corner,
                style: `
                    position: absolute;
                    width: 10px;
                    height: 10px;
                    background: var(--primary);
                    border: 2px solid var(--card);
                    border-radius: 2px;
                    cursor: ${corner}-resize;
                    z-index: 10;
                    opacity: 0;
                    transition: opacity 0.2s;
                    ${corner.includes('n') ? 'top: -5px;' : 'bottom: -5px;'}
                    ${corner.includes('w') ? 'left: -5px;' : 'right: -5px;'}
                `
            });

            handle.addEventListener("mousedown", (e: MouseEvent) => {
                e.stopPropagation();
                resizingTable = {
                    tableId: table.id,
                    corner,
                    startX: e.clientX,
                    startY: e.clientY,
                    startWidth: size.width,
                    startHeight: size.height,
                    startTableX: table.x,
                    startTableY: table.y
                };
            });

            tableEl.appendChild(handle);
        }

        tableEl.addEventListener("mouseenter", () => {
            tableEl.querySelectorAll('.resize-handle').forEach(h => (h as HTMLElement).style.opacity = "1");
        });
        tableEl.addEventListener("mouseleave", () => {
            tableEl.querySelectorAll('.resize-handle').forEach(h => (h as HTMLElement).style.opacity = "0");
        });

        // Table drag handling
        tableEl.addEventListener("mousedown", (e: MouseEvent) => {
            if ((e.target as HTMLElement).classList.contains('resize-handle')) return;
            if ((e.target as HTMLElement).tagName === "BUTTON") return;
            draggedTable = table.id;
            const rect = tableEl.getBoundingClientRect();
            // Adjust drag offset for zoom level
            dragOffset = { x: (e.clientX - rect.left) / zoomLevel, y: (e.clientY - rect.top) / zoomLevel };
        });

        // Render seats
        renderSeatsForTable(tableEl, table, chart, students, size);

        return tableEl;
    }

    function renderSeatsForTable(tableEl: HTMLElement, table: Table, chart: SeatingChart, students: Student[], size: { width: number; height: number }) {
        const seatPositions = calculateSeatPositions(table.shape, table.seats, size);
        // Only show teacher constraint violations, not admin ones
        const allViolations = chart.lastAssignment
            ? checkAssignmentConstraints(chart.lastAssignment, chart, students)
            : [];
        const teacherViolations = allViolations.filter(v => v.constraint.source !== 'admin');
        const violatingStudents = new Set(teacherViolations.flatMap(v => [v.studentA, v.studentB]));

        for (let i = 0; i < table.seats.length; i++) {
            const seat = table.seats[i];
            const pos = seatPositions[i];

            if (!seat || !pos) continue;

            const studentId = chart.lastAssignment?.seatAssignments.get(seat.id);
            const student = studentId ? students.find(s => s.id === studentId) : null;
            const hasViolation = studentId && violatingStudents.has(studentId);

            const seatEl = el("div", {
                class: `seat ${student ? 'occupied' : 'empty'} ${hasViolation ? 'violation' : ''} editor-seat`,
                "data-seat-id": seat.id,
                style: `
                    position: absolute;
                    left: ${pos.x - 18}px;
                    top: ${pos.y - 18}px;
                    cursor: grab;
                `,
                draggable: student ? "true" : undefined
            });

            if (student) {
                // Show avatar if available, otherwise show initials
                if (student.avatarUrl) {
                    seatEl.style.backgroundImage = `url(${student.avatarUrl})`;
                    seatEl.style.backgroundSize = "cover";
                    seatEl.style.backgroundPosition = "center";
                    seatEl.classList.add("has-avatar");
                } else {
                    // Show initials with colored background
                    seatEl.appendChild(el("span", { class: "seat-name" }, generateInitials(student.name)));
                    seatEl.style.backgroundColor = stringToColor(student.id);
                    seatEl.style.color = "white";
                }
                seatEl.title = student.name;

                seatEl.addEventListener("dragstart", (e: DragEvent) => {
                    e.dataTransfer?.setData("text/plain", student.id);
                    e.dataTransfer?.setData("source-seat", seat.id);
                    seatEl.classList.add("dragging");
                });
                seatEl.addEventListener("dragend", () => seatEl.classList.remove("dragging"));
            }

            // Drop handlers for student assignment
            seatEl.addEventListener("dragover", (e: DragEvent) => {
                if (e.dataTransfer?.types.includes("text/plain")) {
                    e.preventDefault();
                    seatEl.classList.add("drag-over");
                }
            });
            seatEl.addEventListener("dragleave", () => seatEl.classList.remove("drag-over"));
            seatEl.addEventListener("drop", (e: DragEvent) => {
                e.preventDefault();
                seatEl.classList.remove("drag-over");
                const droppedStudentId = e.dataTransfer?.getData("text/plain");
                const sourceSeatId = e.dataTransfer?.getData("source-seat");
                if (droppedStudentId) {
                    handleSeatDrop(chart, seat.id, droppedStudentId, sourceSeatId);
                }
            });

            // Seat position dragging
            seatEl.addEventListener("mousedown", (e: MouseEvent) => {
                if ((e.target as HTMLElement).classList.contains('seat-name')) return;
                e.stopPropagation();
                draggedSeat = { tableId: table.id, seatId: seat.id };
                const rect = seatEl.getBoundingClientRect();
                seatDragOffset = { x: e.clientX - rect.left - 18, y: e.clientY - rect.top - 18 };
                seatEl.style.zIndex = "100";
            });

            tableEl.appendChild(seatEl);
        }
    }

    function setupMouseHandlers(canvas: HTMLElement, chart: SeatingChart) {
        canvas.addEventListener("mousemove", (e: MouseEvent) => {
            const canvasRect = canvas.getBoundingClientRect();

            // Table dragging - adjust for zoom level
            if (draggedTable) {
                const tableEl = canvas.querySelector(`[data-table-id="${draggedTable}"]`) as HTMLElement;
                if (tableEl) {
                    // Divide by zoomLevel to convert visual pixels to canvas coordinates
                    const x = (e.clientX - canvasRect.left) / zoomLevel - dragOffset.x;
                    const y = (e.clientY - canvasRect.top) / zoomLevel - dragOffset.y;
                    tableEl.style.left = `${Math.max(0, x)}px`;
                    tableEl.style.top = `${Math.max(0, y)}px`;
                }
            }

            // Seat dragging with edge snapping - adjust for zoom level
            if (draggedSeat) {
                const tableEl = canvas.querySelector(`[data-table-id="${draggedSeat.tableId}"]`) as HTMLElement;
                const seatEl = tableEl?.querySelector(`[data-seat-id="${draggedSeat.seatId}"]`) as HTMLElement;
                if (tableEl && seatEl) {
                    const tableRect = tableEl.getBoundingClientRect();
                    const tableWidth = parseInt(tableEl.style.width);
                    const tableHeight = parseInt(tableEl.style.height);
                    const cx = tableWidth / 2;
                    const cy = tableHeight / 2;
                    // Adjust for zoom when calculating mouse position relative to table
                    const mouseX = (e.clientX - tableRect.left) / zoomLevel;
                    const mouseY = (e.clientY - tableRect.top) / zoomLevel;
                    const shape = tableEl.classList.contains('round') ? 'round' : 'rectangular';
                    const snapped = snapToEdge(mouseX, mouseY, cx, cy, tableWidth, tableHeight, shape);
                    seatEl.style.left = `${snapped.x - 18}px`;
                    seatEl.style.top = `${snapped.y - 18}px`;
                }
            }

            // Marker dragging - adjust for zoom level
            if (draggedMarker) {
                const markerEl = canvas.querySelector(`[data-marker-id="${draggedMarker}"]`) as HTMLElement;
                if (markerEl) {
                    const x = (e.clientX - canvasRect.left) / zoomLevel - markerDragOffset.x;
                    const y = (e.clientY - canvasRect.top) / zoomLevel - markerDragOffset.y;
                    markerEl.style.left = `${Math.max(0, x)}px`;
                    markerEl.style.top = `${Math.max(0, y)}px`;
                }
            }

            // Table resizing - adjust for zoom level
            if (resizingTable) {
                const tableEl = canvas.querySelector(`[data-table-id="${resizingTable.tableId}"]`) as HTMLElement;
                if (tableEl) {
                    // Adjust delta by zoom level
                    const dx = (e.clientX - resizingTable.startX) / zoomLevel;
                    const dy = (e.clientY - resizingTable.startY) / zoomLevel;

                    let newWidth = resizingTable.startWidth;
                    let newHeight = resizingTable.startHeight;
                    let newX = resizingTable.startTableX;
                    let newY = resizingTable.startTableY;

                    if (resizingTable.corner.includes('e')) newWidth = Math.max(80, resizingTable.startWidth + dx);
                    if (resizingTable.corner.includes('w')) {
                        const widthChange = Math.min(dx, resizingTable.startWidth - 80);
                        newWidth = resizingTable.startWidth - widthChange;
                        newX = resizingTable.startTableX + widthChange;
                    }
                    if (resizingTable.corner.includes('s')) newHeight = Math.max(80, resizingTable.startHeight + dy);
                    if (resizingTable.corner.includes('n')) {
                        const heightChange = Math.min(dy, resizingTable.startHeight - 80);
                        newHeight = resizingTable.startHeight - heightChange;
                        newY = resizingTable.startTableY + heightChange;
                    }

                    tableEl.style.width = `${newWidth}px`;
                    tableEl.style.height = `${newHeight}px`;
                    tableEl.style.left = `${Math.max(0, newX)}px`;
                    tableEl.style.top = `${Math.max(0, newY)}px`;
                }
            }
        });

        const handleMouseUp = () => {
            if (draggedTable) {
                const tableEl = canvas.querySelector(`[data-table-id="${draggedTable}"]`) as HTMLElement;
                if (tableEl) {
                    const x = parseInt(tableEl.style.left);
                    const y = parseInt(tableEl.style.top);
                    setState(ns => {
                        const c = ns.classes.find(x => x.id === getState().activeClassId);
                        const ch = c?.seatingCharts?.find(x => x.id === chart.id);
                        const t = ch?.tables.find(x => x.id === draggedTable);
                        if (t) { t.x = x; t.y = y; }
                    });
                }
                draggedTable = null;
            }

            if (draggedSeat) {
                const tableEl = canvas.querySelector(`[data-table-id="${draggedSeat.tableId}"]`) as HTMLElement;
                const seatEl = tableEl?.querySelector(`[data-seat-id="${draggedSeat.seatId}"]`) as HTMLElement;
                if (tableEl && seatEl) {
                    const seatX = parseInt(seatEl.style.left) + 18;
                    const seatY = parseInt(seatEl.style.top) + 18;
                    const tableWidth = parseInt(tableEl.style.width);
                    const tableHeight = parseInt(tableEl.style.height);
                    const cx = tableWidth / 2;
                    const cy = tableHeight / 2;
                    const offsetX = seatX - cx;
                    const offsetY = seatY - cy;

                    setState(ns => {
                        const c = ns.classes.find(x => x.id === getState().activeClassId);
                        const ch = c?.seatingCharts?.find(x => x.id === chart.id);
                        const t = ch?.tables.find(x => x.id === draggedSeat?.tableId);
                        const seat = t?.seats.find(s => s.id === draggedSeat?.seatId);
                        if (seat) { seat.x = offsetX; seat.y = offsetY; }
                    });
                    seatEl.style.zIndex = "";
                }
                draggedSeat = null;
            }

            if (draggedMarker) {
                const markerEl = canvas.querySelector(`[data-marker-id="${draggedMarker}"]`) as HTMLElement;
                if (markerEl) {
                    const x = parseInt(markerEl.style.left);
                    const y = parseInt(markerEl.style.top);
                    setState(ns => {
                        const c = ns.classes.find(x => x.id === getState().activeClassId);
                        const ch = c?.seatingCharts?.find(x => x.id === chart.id);
                        const marker = ch?.referenceMarkers?.find(m => m.id === draggedMarker);
                        if (marker) { marker.x = x; marker.y = y; }
                    });
                    markerEl.style.zIndex = "";
                }
                draggedMarker = null;
            }

            if (resizingTable) {
                const tableEl = canvas.querySelector(`[data-table-id="${resizingTable.tableId}"]`) as HTMLElement;
                if (tableEl) {
                    const newWidth = parseInt(tableEl.style.width);
                    const newHeight = parseInt(tableEl.style.height);
                    const newX = parseInt(tableEl.style.left);
                    const newY = parseInt(tableEl.style.top);

                    setState(ns => {
                        const c = ns.classes.find(x => x.id === getState().activeClassId);
                        const ch = c?.seatingCharts?.find(x => x.id === chart.id);
                        const t = ch?.tables.find(x => x.id === resizingTable?.tableId);
                        if (t) {
                            t.width = newWidth;
                            t.height = newHeight;
                            t.x = newX;
                            t.y = newY;
                        }
                    });
                    render();
                }
                resizingTable = null;
            }
        };

        canvas.addEventListener("mouseup", handleMouseUp);
        canvas.addEventListener("mouseleave", handleMouseUp);
    }

    function handleSeatDrop(chart: SeatingChart, targetSeatId: string, studentId: string, sourceSeatId?: string) {
        const s = getState();
        const cls = s.classes.find(c => c.id === s.activeClassId);
        if (!cls) return;

        pushSeatingHistory(cls.id, "Moved student");

        setState(ns => {
            const c = ns.classes.find(x => x.id === ns.activeClassId);
            const ch = c?.seatingCharts?.find(x => x.id === chart.id);
            if (!ch?.lastAssignment) return;

            const targetStudent = ch.lastAssignment.seatAssignments.get(targetSeatId);

            if (sourceSeatId) {
                if (targetStudent) {
                    ch.lastAssignment.seatAssignments.set(sourceSeatId, targetStudent);
                } else {
                    ch.lastAssignment.seatAssignments.set(sourceSeatId, undefined);
                }
            } else {
                for (const [seatId, sid] of ch.lastAssignment.seatAssignments) {
                    if (sid === studentId) {
                        ch.lastAssignment.seatAssignments.set(seatId, undefined);
                        break;
                    }
                }
            }

            ch.lastAssignment.seatAssignments.set(targetSeatId, studentId);
            ch.lastAssignment.timestamp = Date.now();
        });

        render();
    }

    function renderUnassignedStudents(students: Student[], chart: SeatingChart): HTMLElement {
        const container = el("div", {
            style: "margin-top: 1rem; padding: 1rem; background: var(--bg-secondary); border-radius: 8px;"
        });

        container.appendChild(
            el("h4", { style: "margin: 0 0 0.5rem 0; font-size: 0.875rem;" },
                `Unassigned Students (${students.length})`
            )
        );

        const list = el("div", { style: "display: flex; flex-wrap: wrap; gap: 0.5rem;" });

        for (const student of students) {
            const chip = el("div", {
                class: "student-chip",
                style: "padding: 0.25rem 0.75rem; background: var(--bg); border: 1px solid var(--border); border-radius: 16px; font-size: 0.8125rem; cursor: grab;",
                draggable: "true"
            }, student.name);

            chip.addEventListener("dragstart", (e: DragEvent) => {
                e.dataTransfer?.setData("text/plain", student.id);
            });

            list.appendChild(chip);
        }

        container.appendChild(list);
        return container;
    }

    function getUnassignedStudents(chart: SeatingChart, students: Student[]): Student[] {
        const presentStudents = students.filter(s => !s.absent);

        if (!chart.lastAssignment) {
            return presentStudents;
        }

        const assignedIds = new Set(
            Array.from(chart.lastAssignment.seatAssignments.values()).filter(Boolean)
        );

        return presentStudents.filter(s => !assignedIds.has(s.id));
    }

    // Initial render
    render();

    // Subscribe to updates
    subscribe(render);
    document.addEventListener("seating:update", render);

    return container;
}
